import {createBounds} from "@luciad/ria/shape/ShapeFactory";
import {Map} from "@luciad/ria/view/Map";
import * as React from "react";
import {Button} from "react-bootstrap";
import {defineMessages, InjectedIntlProps, injectIntl} from "react-intl";
import {Bounds, SpatialModel} from "../../../model";
import {Style} from "../../../styles/model";
import {equals} from "../../util/ArrayUtil";
import {FullscreenUtil} from "../../util/FullscreenUtil";
import {Logger} from "../../util/Logger";
import {LcdIcon} from "../icon/LcdIcon";
import {PreviewMetadata} from "./model";
import {getBounds} from "./RiaApi";
import {RIAMap} from "./RIAMap";
import {Api} from "../../../api/InternalApi";

const RESTRICT_NAVIGATION_BOUNDS: Bounds = {
  x: -180,
  width: 360,
  y: -90,
  height: 180,
};

interface PreviewerProps {
  wmsBaseUrl: string;
  api: Api;
  dataToVisualize: SpatialModel[];
  shouldFit: boolean;
  shouldAnimateFit?: boolean;
  height?: string;
  getPreviewMetadataFunction?: (item: SpatialModel) => Promise<PreviewMetadata>;
  hideFitDataButton?: boolean;
  backgroundDataPreview?: boolean;
  backgroundSequenceNumber?: number;
}

interface PreviewerState {
  isFullscreen: boolean;
  isPreviewable: boolean;
  isGeoreferenced: boolean;
  isEmpty: boolean;
  shouldForceToPreview: boolean;
  // Keep track of the data for which the preview metadata has been fetched
  previewData: SpatialModel[];
}

const PREVIEW_MESSAGES = defineMessages({
  showingBoundsOnly: {
    id: "studio.preview.showing-bounds-only",
    defaultMessage: "Showing bounds only.",
  },
  clickToLoadData: {
    id: "studio.preview.click-to-load-data",
    defaultMessage: "Click to load data.",
  },
  loadData: {
    id: "studio.preview.load-data",
    defaultMessage: "Load data.",
  },
  dataNotGeoreferenced: {
    id: "studio.preview.no-geographic-model-reference",
    defaultMessage: "No geographic model reference.",
  },
  dataCantBePreviewed: {
    id: "studio.preview.data-cant-be-previewed",
    defaultMessage: "The data can't be previewed.",
  },
  noData: {
    id: "studio.preview.no-data",
    defaultMessage: "No data.",
  },
  styleCantBePreviewed: {
    id: "studio.preview.style-cant-be-previewed",
    defaultMessage: "The style can't be previewed. The data to preview styles has not been configured.",
  },
  fit: {
    id: "studio.preview.toolbar.fit",
    defaultMessage: "Fit",
  },
  zoomIn: {
    id: "studio.preview.toolbar.zoomIn",
    defaultMessage: "Zoom in",
  },
  zoomOut: {
    id: "studio.preview.toolbar.zoomOut",
    defaultMessage: "Zoom out",
  },
  toggleFullscreen: {
    id: "studio.preview.toolbar.toggleFullscreen",
    defaultMessage: "Toggle fullscreen",
  },
});

class PreviewerComponent extends React.Component<PreviewerProps & InjectedIntlProps, PreviewerState> {

  _logger: Logger = Logger.getLogger("Previewer");

  map: Map;
  _previewerDivRef: HTMLDivElement;
  savedViewExtents = null;
  mapIdleListener = null;

  constructor(props) {
    super(props);
    this.state = {
      isFullscreen: false,
      isPreviewable: false,
      isGeoreferenced: false,
      isEmpty: false,
      shouldForceToPreview: false,
      previewData: [],
    };
  }

  componentDidMount() {
    this.getPreviewMetadata(this.props.dataToVisualize);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // V181-984: To prevent making multiple requests for the same data item, fetch the previewable result if the selected item has been changed.
    if (nextProps.dataToVisualize.length > 0 && !equals(nextProps.dataToVisualize, this.props.dataToVisualize)) {
      this.getPreviewMetadata(nextProps.dataToVisualize);
    }
  }

  fit = () => {
    const {dataToVisualize} = this.props;

    this.map.mapNavigator.fit({
      bounds: getBounds(dataToVisualize),
      animate: {duration: 1000, ease: (n) => n}, // ease should be optional in type definition
      fitMargin: "0px",
    }).catch((error) => {
      this._logger.error("Error occurred while fitting on data", error);
    });
  }

  setMap = (map: Map) => {
    this.map = map;
  }

  handleZoomInClick = () => {
    if (this.map) {
      this.map.mapNavigator.zoom({factor: 2, animate: true});
    }
  }

  handleZoomOutClick = () => {
    if (this.map) {
      this.map.mapNavigator.zoom({factor: 1 / 2, animate: true});
    }
  }

  handlePreviewClick = () => {
    this.setState(Object.assign({}, this.state, {isPreviewable: !this.state.isPreviewable}));
  }

  toggleFullscreen = () => {
    const fullScreenSwitched = () => {
      this.map.resize();
      if (this.savedViewExtents) {
        this.restoreViewExtents(this.savedViewExtents);
        this.savedViewExtents = null;
      }
    };
    const onExit = () => {
      this.setState(Object.assign({}, this.state, {isFullscreen: false}), fullScreenSwitched);
    };
    const onActivate = () => {
      //The user can exit fullscreen at any time using the ESC key.
      //When this happens, there is no way to save the state _before_ the fullscreen exit.
      //The workaround for this is to keep track of map state with a onMapIdle listener, while fullscreen
      this.mapIdleListener = this.map.on("idle", () => {
        this.savedViewExtents = this.saveViewExtents();
      });
      this.setState(Object.assign({}, this.state, {isFullscreen: true}), fullScreenSwitched);
    };
    if (this.state.isFullscreen) {
      //Fullscreen was toggled by clicking on the button, see above for ESC key exit
      this.mapIdleListener.remove();
      this.mapIdleListener = null;
      this.savedViewExtents = this.saveViewExtents();
      FullscreenUtil.exitFullscreen();
    } else {
      this.savedViewExtents = this.saveViewExtents();
      FullscreenUtil.requestFullscreen(this._previewerDivRef, onActivate, onExit);
    }
  }

  saveViewExtents() {
    const viewBounds = createBounds(null, [0, this.map.viewSize[0], 0, this.map.viewSize[1]]);
    return this.map.viewToMapTransformation.transformBounds(viewBounds);
  }

  restoreViewExtents(viewExtents) {
    this.map.mapNavigator.fit({
      bounds: viewExtents,
      animate: {duration: 200, ease: (n) => n}, // ease should be optional in type definition
      fitMargin: "0px",
    }).catch((error) => {
      this._logger.error("Error occurred while restoring view extents", error);
    });
  }

  getPreviewMetadata = (dataToVisualize) => {
    const {getPreviewMetadataFunction} = this.props;

    if (getPreviewMetadataFunction && dataToVisualize && dataToVisualize.length !== 0) {

      const promises = [];
      let isPreviewableResult = true, shouldForceToPreviewResult = true, isGeoreferencedResult = true, emptyResult = true;

      dataToVisualize.forEach((dataItem) => {
        if (dataItem && dataItem.id) {
          const previewMetadataPromise = getPreviewMetadataFunction(dataItem);

          promises.push(previewMetadataPromise);
          previewMetadataPromise.then((previewMetadata: PreviewMetadata) => {
            isPreviewableResult = isPreviewableResult && previewMetadata.previewable;
            shouldForceToPreviewResult = shouldForceToPreviewResult && (previewMetadata.previewable || previewMetadata.shouldForceToPreview);
            isGeoreferencedResult = isGeoreferencedResult && previewMetadata.georeferenced;
            emptyResult = emptyResult && previewMetadata.empty;
          });
        }
      });

      Promise.all(promises).then(() => {
        this.setState({
          isPreviewable: isPreviewableResult,
          isGeoreferenced: isGeoreferencedResult,
          isEmpty: emptyResult,
          shouldForceToPreview: shouldForceToPreviewResult,
          previewData: dataToVisualize
        });
      }).catch((error) => {
        this._logger.error("Error occured while loading preview settings.", error);
      });
    } else {
      this.setState(
          {isPreviewable: true, shouldForceToPreview: false, isGeoreferenced: true, isEmpty: false, previewData: []});
    }
  }

  isPreviewingStyles() {
    const {dataToVisualize} = this.props;
    return dataToVisualize.length > 0 && (dataToVisualize[0] as Style).content !== undefined;
  }

  renderCantPreviewMessage() {
    const {intl} = this.props;
    if (this.isPreviewingStyles()) {
      return <div id="preview-notification">
        {intl.formatMessage(PREVIEW_MESSAGES.styleCantBePreviewed)}
      </div>;
    } else {
      return <div id="preview-notification">
        {intl.formatMessage(PREVIEW_MESSAGES.showingBoundsOnly)}
        <br/>
        {intl.formatMessage(PREVIEW_MESSAGES.dataCantBePreviewed)}
      </div>;
    }
  }

  render() {
    const {wmsBaseUrl, dataToVisualize, shouldFit, shouldAnimateFit, height, hideFitDataButton, intl, backgroundDataPreview, backgroundSequenceNumber} = this.props;
    const {isFullscreen, isPreviewable, shouldForceToPreview, isGeoreferenced, isEmpty, previewData} = this.state;

    const fullScreenStyleOverride = isFullscreen ? {width: "100%", height: "100%"} : {};
    const riaMapHeightNotFullScreen = height || "600px";
    const riaMapHeight = isFullscreen ? "100%" : riaMapHeightNotFullScreen;
    const dataExist = dataToVisualize && dataToVisualize.length !== 0;
    const showNotDataMessage = dataExist && isEmpty;
    const showNotGeoreferencedMessage = dataExist && !isEmpty && !isGeoreferenced;
    const showPreviewButton = dataExist && !isPreviewable && shouldForceToPreview && isGeoreferenced && !isEmpty;
    const showCantPreviewMessage = dataExist && !isPreviewable && !shouldForceToPreview && isGeoreferenced && !isEmpty;
    const showPreview = isPreviewable && isGeoreferenced && !isEmpty && equals(dataToVisualize, previewData);
    const mapData = this.isPreviewingStyles() && !isPreviewable ? [] : dataToVisualize;

    return (
        <div className="previewer" ref={(ref) => this._previewerDivRef = ref} style={fullScreenStyleOverride}>
          <div className="previewToolbar">
            {!hideFitDataButton && <Button className="btn-icon" onClick={() => this.fit()}
                    title={intl.formatMessage(PREVIEW_MESSAGES.fit)}><LcdIcon icon="spot"/></Button>}
            <Button className="btn-icon" onClick={this.handleZoomInClick}
                    title={intl.formatMessage(PREVIEW_MESSAGES.zoomIn)}><LcdIcon icon="zoom-in"/></Button>
            <Button className="btn-icon" onClick={this.handleZoomOutClick}
                    title={intl.formatMessage(PREVIEW_MESSAGES.zoomOut)}><LcdIcon icon="zoom-out"/></Button>
            <Button className="btn-icon" onClick={this.toggleFullscreen}
                    title={intl.formatMessage(PREVIEW_MESSAGES.toggleFullscreen)}>
              <LcdIcon icon={isFullscreen ? "resize-small" : "fullscreen"}/>
            </Button>
          </div>
          {
            showNotDataMessage &&
            <div id="preview-notification">
              {intl.formatMessage(PREVIEW_MESSAGES.dataCantBePreviewed)}
              <br/>
              {intl.formatMessage(PREVIEW_MESSAGES.noData)}
            </div>
          }
          {
            showNotGeoreferencedMessage &&
            <div id="preview-notification">
              {intl.formatMessage(PREVIEW_MESSAGES.dataCantBePreviewed)}
              <br/>
              {intl.formatMessage(PREVIEW_MESSAGES.dataNotGeoreferenced)}
            </div>
          }
          {
            !showCantPreviewMessage && showPreviewButton &&
            <Button className="btn-icon" id="preview-button" onClick={this.handlePreviewClick}
                    title={intl.formatMessage(PREVIEW_MESSAGES.loadData)}>
              {intl.formatMessage(PREVIEW_MESSAGES.showingBoundsOnly)}
              <br/>
              {intl.formatMessage(PREVIEW_MESSAGES.clickToLoadData)}
            </Button>
          }
          {
            showCantPreviewMessage && !showPreviewButton && this.renderCantPreviewMessage()
          }
          <div className="previewOverlay"></div>
          <RIAMap style={{width: "100%", height: riaMapHeight}}
                  api={this.props.api}
                  wmsUrl={wmsBaseUrl}
                  dataToVisualize={mapData}
                  shouldFit={shouldFit}
                  shouldAnimateFit={shouldAnimateFit || false}
                  shouldHideLayerTree={true}
                  shouldAddGridLayer={false}
                  restrictNavigationBounds={RESTRICT_NAVIGATION_BOUNDS}
                  onMapInitialized={this.setMap}
                  showPreview={showPreview}
                  backgroundDataPreview={backgroundDataPreview}
                  backgroundSequenceNumber={backgroundSequenceNumber}
          />
        </div>
    );
  }
}

export const Previewer = injectIntl(PreviewerComponent);
